import React from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { t } from "i18next";

import DuetImg from "../../shared/img/config_icons/Duet_transparent.png";
import PartyImg from "../../shared/img/config_icons/Party_transparent.png";
import PicassoImg from "../../shared/img/config_icons/Picasso_transparent.png";
import RankImg from "../../shared/img/config_icons/Rank_transparent.png";
import CoOpImg from "../../shared/img/config_icons/CoOp_transparent.png";
import VersusImg from "../../shared/img/config_icons/Versus_transparent.png";

// ─── Entry mode: Co-op (predict your friends) vs Versus (everyone answers at once) ───────
export type EntryMode = "VERSUS" | "COOP";

// ─── Presets available under each entry mode ─────────────────────────────────────────────
export type PresetId = "RANK" | "DUET" | "PARTY" | "PICASSO" | "CUSTOM";

export interface PresetConfig {
  enabledModes: string[];
  roundLimit: number;
  roundTimer: number;
  maxSkips: number;
  waitForHost: boolean;
  enableJoinDuringRound: boolean;
}

/**
 * Co-op is listed first — it's the original game and the gentler starting point.
 *
 * `minPlayers` is surfaced on the tile because it's the difference that most often blocks a
 * lobby: versus scores everyone against the group's aggregate ranking, which needs an actual
 * group — with two players the Borda count comes down to the submission-order tie-break.
 */
export const ENTRY_DEFS: { id: EntryMode; label: string; subtext: string; img: string; color: string; minPlayers: number }[] = [
  { id: "COOP", label: "entry_coop", subtext: "entry_coop_desc", img: CoOpImg, color: "#D36667", minPlayers: 2 },
  { id: "VERSUS", label: "entry_versus", subtext: "entry_versus_desc", img: VersusImg, color: "#7F97C2", minPlayers: 3 },
];

interface PresetDef {
  id: PresetId;
  /** Kept for reference and for any non-tile surface — the tiles themselves don't render it,
   *  because each preset image already has its name in the artwork. */
  label: string;
  description: string;
}

/**
 * Presets are per family — the two families have genuinely different mode pools, so a single
 * shared grid would offer combinations that can't exist. REVERSE_ROUND appears in no preset and
 * is no longer offered in the Custom tab either — the mode is retired from the lobby.
 */
export const PRESET_DEFS: Record<EntryMode, PresetDef[]> = {
  COOP: [
    { id: "DUET", label: "lobby_preset_duet", description: "lobby_preset_duet_desc" },
    { id: "PARTY", label: "lobby_preset_party", description: "lobby_preset_party_desc" },
    { id: "PICASSO", label: "lobby_preset_picasso", description: "lobby_preset_picasso_desc" },
  ],
  VERSUS: [
    { id: "RANK", label: "lobby_preset_rank", description: "lobby_preset_rank_desc" },
    { id: "PARTY", label: "lobby_preset_party", description: "lobby_preset_party_versus_desc" },
  ],
};

const PRESET_COLORS: Record<PresetId, string> = {
  RANK: "#7F97C2", DUET: "#D36667", PARTY: "#A7C577", PICASSO: "#CA8755", CUSTOM: "#9B8EC4",
};

const PRESET_IMAGES: Partial<Record<PresetId, string>> = {
  RANK: RankImg, DUET: DuetImg, PARTY: PartyImg, PICASSO: PicassoImg,
};

/**
 * Preset → concrete config.
 *
 * Co-op modes are czar-based: one player answers about themselves, everyone else predicts. A
 * round is a full czar rotation, so a co-op match is roundLimit × playerCount questions — which
 * is why the co-op round limits stay small.
 *
 * Versus modes have no czar: everyone answers at once and is scored against the group. One turn
 * is one round, so roundLimit is literally the number of questions and can safely be much higher.
 */
export function getPresetConfig(preset: PresetId, playerCount: number, entryMode: EntryMode): PresetConfig {
  const common = { roundTimer: 300, maxSkips: 8, waitForHost: false, enableJoinDuringRound: true };

  if (entryMode === "VERSUS") {
    switch (preset) {
      case "PARTY": {
        const modes = ["STANDARD_VERSUS"];
        if (playerCount >= 3) modes.push("RUN_WITH_THE_HERD"); // Herd needs 3+
        return { enabledModes: modes, roundLimit: 10, ...common };
      }
      case "RANK":
      case "CUSTOM":
      default:
        return { enabledModes: ["STANDARD_VERSUS"], roundLimit: 10, ...common };
    }
  }

  switch (preset) {
    case "DUET":
      return { enabledModes: ["STANDARD"], roundLimit: 5, ...common };
    case "PARTY": {
      const modes = ["STANDARD", "PICASSO"];
      if (playerCount >= 3) modes.push("FOLLOW_THE_LEADER"); // FTL needs 3+
      // A co-op round costs one question per player, so bigger groups get fewer rounds.
      return { enabledModes: modes, roundLimit: playerCount > 3 ? 2 : 3, ...common };
    }
    case "PICASSO":
      return { enabledModes: ["PICASSO"], roundLimit: 5, ...common };
    case "CUSTOM":
    default:
      return { enabledModes: ["STANDARD", "FOLLOW_THE_LEADER", "PICASSO"], roundLimit: 3, ...common };
  }
}

export function getAutoPreset(playerCount: number, entryMode: EntryMode): PresetId {
  if (entryMode === "VERSUS") return playerCount <= 2 ? "RANK" : "PARTY";
  return playerCount <= 2 ? "DUET" : "PARTY";
}

/** Try to match a received config against the presets of a given entry mode. */
export function detectPreset(
  config: { enabledModes: string[]; roundLimit: number; roundTimer: number; maxSkips: number; waitForHost: boolean; enableJoinDuringRound: boolean },
  playerCount: number,
  entryMode: EntryMode,
): PresetId {
  for (const { id } of PRESET_DEFS[entryMode]) {
    const expected = getPresetConfig(id, playerCount, entryMode);
    if (
      expected.roundLimit === config.roundLimit &&
      expected.roundTimer === config.roundTimer &&
      expected.maxSkips === config.maxSkips &&
      expected.waitForHost === config.waitForHost &&
      expected.enableJoinDuringRound === config.enableJoinDuringRound &&
      expected.enabledModes.length === config.enabledModes.length &&
      expected.enabledModes.every((m) => config.enabledModes.includes(m))
    ) {
      return id;
    }
  }
  return "CUSTOM";
}

/** Infer the entry mode from a received config (versus family iff STANDARD_VERSUS or Herd present). */
export function detectEntryMode(enabledModes: string[]): EntryMode {
  return enabledModes.includes("STANDARD_VERSUS") || enabledModes.includes("RUN_WITH_THE_HERD") ? "VERSUS" : "COOP";
}

// ─── Entry-mode selector: two big tiles shown before the presets ─────────────────────────
interface EntryProps {
  selected: EntryMode | null;
  onSelect: (mode: EntryMode) => void;
  disabled?: boolean;
}

export function GameModeEntrySelector({ selected, onSelect, disabled }: EntryProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 1.25,
        width: "100%",
        minWidth: 0,
      }}
    >
      {ENTRY_DEFS.map((entry) => {
        const isSelected = selected === entry.id;
        return (
          <Box
            key={entry.id}
            onClick={() => !disabled && onSelect(entry.id)}
            sx={{
              position: "relative",
              borderRadius: 2,
              cursor: disabled ? "default" : "pointer",
              border: "2px solid",
              borderColor: isSelected ? entry.color : "transparent",
              bgcolor: isSelected ? `${entry.color}20` : "rgba(0,0,0,0.03)",
              transition: "all 0.15s ease",
              "@media (hover: hover)": disabled ? {} : { "&:hover": { borderColor: entry.color, bgcolor: `${entry.color}14`, "& img": { filter: `drop-shadow(0 2px 6px ${entry.color}66)` } } },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              aspectRatio: "1 / 1",
              p: 1.5,
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <Box
              component="img"
              src={entry.img}
              alt=""
              sx={{
                width: "85%",
                maxWidth: "100%",
                flex: "1 1 auto",
                minHeight: 0,
                objectFit: "contain",
                mb: 0.75,
                opacity: isSelected || selected === null ? 1 : 0.55,
                filter: isSelected ? `drop-shadow(0 3px 7px ${entry.color}66)` : "none",
                transition: "opacity 0.15s, filter 0.15s",
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ width: "100%", minWidth: 0, lineHeight: 1.3, overflowWrap: "anywhere" }}
            >
              {t(entry.subtext)}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ width: "100%", minWidth: 0, color: entry.color, lineHeight: 1.3, mt: 0.25, overflowWrap: "anywhere" }}
            >
              {t("lobby_min_players", { n: entry.minPlayers })}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Compact read-only badge of the chosen family — lets non-hosts see what the host picked. */
export function EntryModeBadge({ mode }: { mode: EntryMode }) {
  const entry = ENTRY_DEFS.find((e) => e.id === mode) ?? ENTRY_DEFS[0];
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ bgcolor: `${entry.color}1A`, borderRadius: 99, pl: 0.5, pr: 1.25, py: 0.25 }}
    >
      <Box component="img" src={entry.img} alt="" sx={{ height: 22, objectFit: "contain" }} />
      <Typography variant="caption" fontWeight={700} sx={{ color: entry.color, letterSpacing: "0.03em" }}>
        {t(entry.label)}
      </Typography>
    </Stack>
  );
}

interface Props {
  entryMode: EntryMode;
  selected: PresetId;
  onSelect: (preset: PresetId) => void;
  onOpenCustom: () => void;
  disabled?: boolean;
}

export default function LobbyPresetSelector({ entryMode, selected, onSelect, onOpenCustom, disabled }: Props) {
  const presets = PRESET_DEFS[entryMode];
  // Two presets fill a single row of the 2-column grid, leaving room to show the art bigger.
  // Three wrap onto a second row, where the existing size already fills the panel.
  const singleRow = presets.length <= 2;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gridAutoRows: "1fr",
        gap: 1,
        width: "100%",
        minWidth: 0,
      }}
    >
      {presets.map((preset) => {
        const isSelected = selected === preset.id;
        const color = PRESET_COLORS[preset.id];
        const img = PRESET_IMAGES[preset.id];
        return (
          <Box
            key={preset.id}
            onClick={() => !disabled && onSelect(preset.id)}
            sx={{
              position: "relative",
              borderRadius: 2,
              cursor: disabled ? "default" : "pointer",
              border: "2px solid",
              borderColor: isSelected ? color : "transparent",
              bgcolor: isSelected ? `${color}20` : "rgba(0,0,0,0.03)",
              transition: "all 0.15s ease",
              "@media (hover: hover)": disabled ? {} : { "&:hover": { borderColor: color, bgcolor: `${color}14`, "& img": { opacity: 0.85, filter: `drop-shadow(0 2px 5px ${color}55)` } } },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 1,
              pb: 1,
              px: 0.5,
              overflow: "hidden",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {img && (
              <Box
                component="img"
                src={img}
                alt=""
                sx={{
                  width: singleRow ? "88%" : "75%",
                  maxWidth: "100%",
                  maxHeight: singleRow ? 190 : 100,
                  objectFit: "contain",
                  opacity: isSelected ? 1 : 0.55,
                  filter: isSelected ? `drop-shadow(0 3px 6px ${color}66)` : "none",
                  transition: "opacity 0.15s, filter 0.15s",
                  display: "block",
                }}
              />
            )}
            {/* No name here — every preset image has its title baked into the artwork. */}
            <Typography
              variant="caption"
              color={isSelected ? color : "text.secondary"}
              fontWeight={isSelected ? 600 : 400}
              textAlign="center"
              sx={{
                width: "100%",
                minWidth: 0,
                lineHeight: 1.3,
                display: "block",
                mt: "auto",
                pt: 0.5,
                px: 0.5,
                overflowWrap: "anywhere",
              }}
            >
              {t(preset.description)}
            </Typography>
            {isSelected && !disabled && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onOpenCustom(); }}
                sx={{ position: "absolute", top: 4, right: 4, opacity: 0.5, p: 0.25 }}
              >
                <SettingsIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
